import { z } from 'zod';
import { PlaceStatusSchema, type VerificationStatus } from './enums';

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? null : value))
    .nullable();

/**
 * What the console may change on a place. The database still checks every rule.
 * Not fsq_id: it comes from the FSQ import only (the database refuses a change,
 * hint fsq_id_locked). Changing the name unpublishes the place and resets its
 * location verification; changing or clearing google_place_id resets the
 * location and the business status (migration 20261115000000).
 */
export const PlaceUpdateSchema = z.object({
  name: z.string().trim().min(2).max(120),
  address: optionalText(200),
  zone_id: z.number().int().positive().nullable(),
  status: PlaceStatusSchema,
  price_level: z.number().int().min(1).max(4).nullable(),
  google_place_id: optionalText(300),
});
export type PlaceUpdate = z.infer<typeof PlaceUpdateSchema>;
export type PlaceUpdateInput = z.input<typeof PlaceUpdateSchema>;

/** The two facts a place needs verified before it can be published. */
export const PUBLISH_FIELDS = ['location', 'business_status'] as const;
export type PublishField = (typeof PUBLISH_FIELDS)[number];

export interface FactState {
  readonly field: string;
  readonly status: VerificationStatus;
  readonly expires_at: string | null;
}

/** One word for the console list: the weakest state among location and status. */
export type PlaceVerification = 'verified' | 'conflicting' | 'expired' | 'single_source' | 'none';

function isFresh(fact: FactState, now: Date): boolean {
  return fact.status === 'verified' && fact.expires_at !== null && new Date(fact.expires_at) > now;
}

export function summarizeVerification(
  facts: readonly FactState[],
  now = new Date(),
): PlaceVerification {
  const relevant = PUBLISH_FIELDS.map((field) => facts.find((fact) => fact.field === field));
  if (relevant.some((fact) => fact?.status === 'conflicting')) return 'conflicting';
  if (relevant.every((fact) => fact !== undefined && isFresh(fact, now))) return 'verified';
  if (
    relevant.some(
      (fact) => fact?.status === 'expired' || (fact?.status === 'verified' && !isFresh(fact, now)),
    )
  ) {
    return 'expired';
  }
  if (relevant.some((fact) => fact !== undefined)) return 'single_source';
  return 'none';
}

/** Mirrors private.is_top_eligible(): the database has the last word when publishing. */
export function canPublish(facts: readonly FactState[], now = new Date()): boolean {
  return summarizeVerification(facts, now) === 'verified';
}
